import get from 'lodash/get';
import React from 'react';
import { FormattedDate, FormattedMessage } from 'react-intl';
import { Link } from 'react-router';
import { push, replace } from 'react-router-redux';
import { all, call, delay, fork, put, putResolve, race, select, take } from 'redux-saga/effects';

import * as PaymentsActions from '../../actions';
import SubscriptionModal from '../../components/Subscription/SubscriptionModal';
import SwitchPlanConfirmationModal from '../../components/SwitchPlanConfirmationModal/SwitchPlanConfirmationModal';
import * as PaymentsConstants from '../../constants';
import * as D from '../../deps';
import * as PaymentsSelectors from '../../selectors';
import * as PaymentsServices from '../../services';
import * as PaymentsUtils from '../../utils';

import * as Helpers from './helpers';

const log = D.Logger.child({ component: 'src/domains/payments/sagas/workers/Payments' }, true);
const spawn = D.taskManagerSpawnFactory(PaymentsConstants.DOMAIN_NAME);

export function* onListenerAuthenticated({ payload }) {
  const isLoggedInUser = yield select(D.Utils.Listener.isLoggedIn);
  const isAnonListener = yield select(D.Utils.Listener.isAnonymousListener);
  if (isLoggedInUser && !isAnonListener) {
    // make sure we only initialize once a user has logged in
    yield spawn(loadPaymentsData);

    yield race([
      take(D.Constants.Api.ApiActions.GET_SUBSCRIPTION_INFO_V2_SUCCESS),
      take(D.Constants.Api.ApiActions.GET_SUBSCRIPTION_INFO_V2_FAIL),
    ]);

    const isPartnerUpgradeFlow = yield select(PaymentsSelectors.isPartnerUpgradeFlow);
    if (!isPartnerUpgradeFlow) {
      yield spawn(checkSubscriptionAndUpsell);
    }
  }

  yield put(PaymentsActions.listenerAndSubscriptionReady(payload.authType));
}

function* logAnonymousStartTrialClick(tier, discountType) {
  if (tier && discountType) {
    yield put(
      D.getInDomain('stats.actions.logWebRegistrationEvent')({
        registrationEventAction: PaymentsUtils.getPlanWebRegistration(tier, discountType),
      }),
    );
    yield put(
      D.Actions.UseCases.linkClicked(PaymentsUtils.getPlanMobileLanding(tier, discountType)),
    );
  }
}

export function* anonymousStartTrialClicked({ tier, discountType, shouldSetAnon }) {
  if (shouldSetAnon) {
    yield put(PaymentsActions.setIsAnonymousUpgrade());
  }

  yield D.tryCall(logAnonymousStartTrialClick, tier, discountType);

  const paymentPath = yield call(PaymentsUtils.getPaymentPath, tier, discountType);
  const redirectUrl = paymentPath ? paymentPath : yield call(D.Utils.Route.getRegisterPath);
  yield put(push(redirectUrl));
}

export function* getSubscriptionInfoV2() {
  yield D.tryCall(PaymentsServices.getSubscriptionInfoV2);
}

/**
 * Guards against the edge case that a listener is on the subscription management page
 * when their subscription expires, leaving them in an inalid state, since only listeners with a subscription
 * can view the subscription management page.
 *
 * @param {object} action -
 */
export function* onGetSubscriptionInfoV2Success(action) {
  const productTier = get(action, 'response.activeProduct.productTier');
  if (productTier === PaymentsConstants.tiers.AD_SUPPORTED) {
    const currentPath = D.getCurrentUrlPath();
    const subscriptionSettingsPath = D.Utils.Route.getSubscriptionPath();

    if (currentPath === subscriptionSettingsPath) {
      yield put(push(D.Utils.Route.getUpgradePath()));
    }
  }
}

export function* getCreditCardV2() {
  yield D.tryCall(PaymentsServices.getCreditCardV2);
}

export function* cancelSubscription(action) {
  yield D.tryCall(PaymentsServices.updateBillingInfoV2, {
    cancelSubscription: true,
    ...action.data,
  });
  yield call(
    Helpers.logSubscriptionWebViewEvent,
    PaymentsConstants.Analytics.CANCEL_SUBSCRIPTION_CONFIRMATION,
  );
}

export function* deleteCreditCard(action) {
  yield D.tryCall(PaymentsServices.updateBillingInfoV2, {
    deleteCreditCard: true,
    ...action.data,
  });
}

export function* resubscribe() {
  yield D.tryCall(PaymentsServices.updateBillingInfoV2, {
    cancelSubscription: false,
  });
}

export function* getAvailableProducts() {
  yield D.tryCall(PaymentsServices.getAvailableProducts);
}

export function* loadPaymentsData() {
  try {
    yield call(PaymentsServices.getSubscriptionInfoV2);
    yield call(PaymentsServices.getAvailableProducts);
    yield call(PaymentsServices.getCreditCardV2);
  } catch (error) {
    yield call(Helpers.handleGenericSubscriptionFailureWithMessage, {
      errorString:
        !!error && !!error.getMessage ? error.getMessage() : 'API error on payments page',
      message: PaymentsConstants.Messages.somethingHasGoneWrongRefreshRetry,
    });
  }
  yield spawn(loadFamilyPlanData);
}

export function* loadFamilyPlanData() {
  const isFamilyPlanMemberChild = yield select(PaymentsSelectors.isFamilyPlanMemberChild);
  const isFamilyPlanMemberParent = yield select(PaymentsSelectors.isFamilyPlanMemberParent);

  if (isFamilyPlanMemberChild) {
    yield D.tryCall(PaymentsServices.getParentFamilyPlanInfo);
  }
  if (isFamilyPlanMemberParent) {
    yield D.tryCall(PaymentsServices.getFamilyPlanRecord);
  }
}

export function* updateCreditCardInfo() {
  const newCreditCardInfo = yield select(PaymentsSelectors.getCreditCardFormValues);
  newCreditCardInfo.cardCompany = PaymentsUtils.getCardType({
    value: newCreditCardInfo.cardNumber,
  });

  const { payload, transactionId } = yield call(PaymentsUtils.getClientProfile);

  const newData = Object.assign(
    {},
    {
      newCreditCardInfo,
      apld: payload,
      atid: transactionId,
    },
  );

  try {
    yield call(PaymentsServices.updateBillingInfoV2, {
      cancelSubscription: false,
      ...newData,
    });
  } catch (error) {
    log.error({ apiError: error }, 'Failed updating credit card info');
  } finally {
    yield call(PaymentsUtils.renewClientProfile);
  }

  yield call(
    Helpers.logSubscriptionWebViewEvent,
    PaymentsConstants.Analytics.UPDATE_PAYMENT_CONFIRMATION,
  );
}

export function* updateBillingInfoSuccess(action) {
  yield put(PaymentsActions.resetCreditCardForm());
  yield put(PaymentsActions.loadPaymentsData());

  const activeTier = yield select(PaymentsSelectors.getActiveProductGroupTier);
  const activeDiscountType = yield select(PaymentsSelectors.getActiveProductDiscountType);
  const planName = PaymentsUtils.getPlanName(activeTier, activeDiscountType);

  let message;
  if (action.params.content.deleteCreditCard || action.params.content.cancelSubscription) {
    message = (
      <FormattedMessage
        {...PaymentsConstants.Messages.canceledNotification}
        values={{ planName }}
      />
    );
  } else {
    message = <FormattedMessage {...PaymentsConstants.Messages.changesSaved} />;
  }

  yield putResolve(
    D.Actions.Overlay.openOverlay({
      [D.Constants.Overlay.OVERLAY_CONFIG.TYPE]: D.Constants.Overlay.OVERLAY_TYPES.blueBar,
      [D.Constants.Overlay.OVERLAY_CONFIG.SUBTYPE]:
        D.Constants.Overlay.SubscriptionMessageBlueBarSubtype,
      [D.Constants.Overlay.OVERLAY_CONFIG.PROPS]: {
        message,
      },
    }),
  );
}

export function* onAcquireProductSuccess({ params }) {
  const { tierName, vendorSku, paymentProvider, specialOfferType, specialOfferToken } = get(
    params,
    'content',
    {},
  );
  const { discountType, previousTier, previousDiscountType } = get(params, 'actionParams', {});

  try {
    yield call(Helpers.processAcquireProductSuccess, {
      tierName,
      discountType,
      previousTier,
      previousDiscountType,
      vendorSku,
      paymentProvider,
      specialOfferType,
      specialOfferToken,
    });

    yield put(PaymentsActions.acquireOfferSuccess({ specialOfferToken, sku: vendorSku }));
    yield put(PaymentsActions.hasFinishedPurchase());
  } catch (e) {
    log.error(`Failed processing acquireProductSuccess: ${e}`);
  }
}

export function* cancelCurrentProduct() {
  yield D.tryCall(PaymentsServices.cancelCurrentProduct);
}

export function* cancelCurrentProductSuccess() {
  yield call(D.putAndWait, D.Actions.Application.listenerReauth());
  yield put(push(D.Utils.Route.getNowPlayingPath()));
}

export function closeExpiredPremiumSubscriptionModal() {
  const { dispatch } = D.getStore();
  dispatch(D.Actions.Modal.closeModal());
}

function closeExpiredSubscriptionOverlay() {
  const { dispatch } = D.getStore();
  dispatch(
    D.Actions.Overlay.closeOverlay({
      [D.Constants.Overlay.OVERLAY_CONFIG.TYPE]: D.Constants.Overlay.OVERLAY_TYPES.blueBar,
      [D.Constants.Overlay.OVERLAY_CONFIG.SUBTYPE]:
        D.Constants.Overlay.SubscriptionMessageBlueBarSubtype,
    }),
  );
}

export function* handleExpiredSubscriber() {
  const hereLink = (
    <Link
      onClick={closeExpiredSubscriptionOverlay}
      className='BlueBar__link'
      to={D.Utils.Route.getUpgradePath()}
    >
      <FormattedMessage {...PaymentsConstants.Messages.here} />
    </Link>
  );

  yield put(
    D.Actions.Overlay.openOverlay({
      [D.Constants.Overlay.OVERLAY_CONFIG.TYPE]: D.Constants.Overlay.OVERLAY_TYPES.blueBar,
      [D.Constants.Overlay.OVERLAY_CONFIG.SUBTYPE]:
        D.Constants.Overlay.SubscriptionMessageBlueBarSubtype,
      [D.Constants.Overlay.OVERLAY_CONFIG.PROPS]: {
        message: (
          <FormattedMessage
            {...PaymentsConstants.Messages.expiredSubscriberNotification}
            values={{ hereLink }}
          />
        ),
        onClose: closeExpiredSubscriptionOverlay,
      },
    }),
  );
}

export function* handleExpiredPremiumSubscriber() {
  const resubscribeLink = (
    <Link
      onClick={closeExpiredPremiumSubscriptionModal}
      className='Modal__container__link'
      to={D.Utils.Route.getUpgradePath()}
    >
      <FormattedMessage {...PaymentsConstants.Messages.expiredPremiumSubscriberReSubscribe} />
    </Link>
  );
  const titleMessage = (
    <FormattedMessage {...PaymentsConstants.Messages.expiredPremiumSubscriberNotificationTitle} />
  );
  const bodyMessage = (
    <FormattedMessage
      {...PaymentsConstants.Messages.expiredPremiumSubscriberNotificationBody}
      values={{ resubscribeLink }}
    />
  );
  const buttonCTAs = [
    <D.Button onClick={closeExpiredPremiumSubscriptionModal} key={1}>
      <FormattedMessage {...PaymentsConstants.Messages.close} />
    </D.Button>,
  ];

  yield put(
    D.Actions.Modal.openModal({
      modalType: 'ModalStandard',
      title: titleMessage,
      body: bodyMessage,
      hideXRow: true,
      buttons: buttonCTAs,
    }),
  );

  const isMobileDevice = yield select(D.Utils.Capabilities.isMobileDevice);
  if (isMobileDevice) return;

  const stationId = yield select(D.getInDomain('playback.utils.getLastPlayedStationId', null));
  if (stationId) {
    const myStationsPath = yield select(D.Utils.Route.getMyStationsPath);
    yield put(push(myStationsPath));
    yield put(
      D.Actions.UseCases.playResumeSource({
        id: stationId,
        pandoraType: D.Constants.SourceTypes.PandoraTypes.USER_STATION,
      }),
    );
  }
}

export function* handleSubscriptionIsSoonToExpire() {
  const activeTier = yield select(PaymentsSelectors.getActiveProductGroupTier);
  const activeDiscountType = yield select(PaymentsSelectors.getActiveProductDiscountType);
  const planName = yield call(PaymentsUtils.getPlanName, activeTier, activeDiscountType);
  const nextBillingDate = yield select(PaymentsSelectors.getNextBillingDate);
  const formattedDate = <FormattedDate value={new Date(nextBillingDate)} />;

  yield put(
    D.Actions.Overlay.openOverlay({
      [D.Constants.Overlay.OVERLAY_CONFIG.TYPE]: D.Constants.Overlay.OVERLAY_TYPES.blueBar,
      [D.Constants.Overlay.OVERLAY_CONFIG.SUBTYPE]:
        D.Constants.Overlay.SubscriptionMessageBlueBarSubtype,
      [D.Constants.Overlay.OVERLAY_CONFIG.PROPS]: {
        message: (
          <FormattedMessage
            {...PaymentsConstants.Messages.expiringSubscriptionPleaseResubscribe}
            values={{ planName, date: formattedDate }}
          />
        ),
        onClose: closeExpiredSubscriptionOverlay,
        buttonText: <FormattedMessage {...PaymentsConstants.Messages.renewNow} />,
        buttonLink: D.Utils.Route.getBillingPath(),
      },
    }),
  );
}

export function* checkSubscriptionAndUpsell() {
  const isExpiredSubscriber = yield select(
    D.Utils.Listener.isFeatureEnabled,
    D.Constants.FeatureFlags.FEATURES.expiredSubscriber,
  );
  const isExpiredPremiumSubscriber = yield select(
    D.Utils.Listener.isFeatureEnabled,
    D.Constants.FeatureFlags.FEATURES.expiredPremiumSubscriber,
  );
  const isSubscriptionSoonToExpire = yield select(PaymentsSelectors.isSubscriptionSoonToExpire);

  if (isExpiredPremiumSubscriber) {
    yield handleExpiredPremiumSubscriber();
  } else if (isExpiredSubscriber) {
    yield handleExpiredSubscriber();
  } else if (isSubscriptionSoonToExpire) {
    yield handleSubscriptionIsSoonToExpire();
  }
}

export function* onEnterPaypalComplete() {
  const isAuthenticatedListener = yield select(D.Utils.Listener.isAuthenticatedListener);

  if (isAuthenticatedListener) {
    log.info('onEnterPaypalComplete(): handling paypal redirect');
    yield call(Helpers.completePaypal);
  } else {
    log.error('onEnterPaypalComplete(): listener is not authenticated');
    yield put(push(D.Utils.Route.getUpgradePath()));
    yield put(
      D.Actions.UseCases.openErrorToast(
        PaymentsConstants.Messages.somethingHasGoneWrongRefreshRetry,
      ),
    );
  }
}

// Leaving as a Family Plan Child account holder
export function* exitFromFamilyPlan() {
  yield D.tryCall(PaymentsServices.exitFromFamilyPlan);
}

export function* createFamilyPlanInvite(action) {
  const emailAddress = get(action, 'data.emailAddress');
  const { error } = yield D.tryCallYield(PaymentsServices.createFamilyPlanInvite, { emailAddress });
  if (error && error.errorType === PaymentsConstants.ErrorCodes.FAMILY_PLAN_ALREADY_MEMBER) {
    yield put(
      D.Actions.Modal.openModalCoachmark({
        ...PaymentsConstants.Messages.alreadyInvitedToJoinPremiumFamily,
        values: { emailAddress },
      }),
    );
  }
}

export function* resendFamilyPlanInvite(action) {
  const emailAddress = get(action, 'data.emailAddress');

  yield D.tryCall(PaymentsServices.resendFamilyPlanInvite, { emailAddress });
}

export function* createFamilyPlanSuccess(action) {
  yield put(
    D.Actions.Modal.openModalCoachmark({
      ...PaymentsConstants.Messages.invitedToJoinFamilyPlan,
      values: { emailAddress: action.params.content.emailAddress },
    }),
  );
  yield fork(loadPaymentsData);
}

export function* resendFamilyPlanSuccess(action) {
  yield put(
    D.Actions.Modal.openModalCoachmark({
      ...PaymentsConstants.Messages.resentInviteToFamilyPlan,
      values: { emailAddress: action.params.content.emailAddress },
    }),
  );
  yield fork(loadPaymentsData);
}

export function* revokeFamilyPlanInvite(action) {
  const inviteToken = get(action, 'data.inviteToken');
  const emailAddress = get(action, 'data.emailAddress');

  yield D.tryCall(PaymentsServices.revokeFamilyPlanInvite, {
    inviteToken,
    emailAddress,
  });
}

export function* revokeFamilyPlanInviteSuccess(action) {
  yield put(
    D.Actions.Modal.openModalCoachmark({
      ...PaymentsConstants.Messages.revokedInviteToFamilyPlan,
      values: { emailAddress: action.params.content.emailAddress },
    }),
  );
  yield fork(loadPaymentsData);
}

export function* removeChildFromFamilyPlan(action) {
  const childListenerID = get(action, 'data.childListenerID');
  const emailAddress = get(action, 'data.emailAddress');

  yield D.tryCall(PaymentsServices.removeChildFromFamilyPlan, {
    childListenerID,
    emailAddress,
  });
}

export function* removeChildFromFamilyPlanSuccess(action) {
  yield put(
    D.Actions.Modal.openModalCoachmark({
      ...PaymentsConstants.Messages.removedChildFamilyPlan,
      values: { emailAddress: action.params.content.emailAddress },
    }),
  );
  yield fork(loadPaymentsData);
}

export function* onAnonLoginSuccess() {
  yield spawn(PaymentsServices.getAvailableProducts);
}

// This is triggered from the /settings/family/invite, /settings/family/manage and /family/invite routes's
// onEnter hooks since the subscriptionInfoV2 data likely won't be available from within the onEnter handlers
// if those routes are loaded directly
export function* determineFamilyPlanManagement(isInviting) {
  const subscriptionInfoV2 = yield select(PaymentsSelectors.getSubscriptionInfoV2);
  if (subscriptionInfoV2.isEmpty()) {
    yield D.tryCall(PaymentsServices.getSubscriptionInfoV2);
  }

  yield D.tryCall(loadFamilyPlanData);

  const isFamilyPlanMemberParent = yield select(PaymentsSelectors.isFamilyPlanMemberParent);
  if (isFamilyPlanMemberParent) {
    // sets the state for the subscription page to display the management menu and first time invite state
    yield put(PaymentsActions.startManagingFamilyPlanAccounts(true, isInviting));
  }
}

export function* onEnterFamilyPlanManagement(action) {
  const { currentPath } = action.data;
  const isListenerLoggedIn = yield select(D.Utils.Listener.isLoggedIn);
  const isAnonymousListener = yield select(D.Utils.Listener.isAnonymousListener);
  if (isListenerLoggedIn && !isAnonymousListener) {
    const isInviting = currentPath.includes(D.Utils.Route.getFamilyPlanManagementPath());
    yield fork(determineFamilyPlanManagement, isInviting);
    yield put(replace(D.Utils.Route.getSubscriptionPath()));
    return;
  }
  yield put(D.Actions.Login.setAfterLoginRedirect(currentPath));
  yield put(replace(D.Utils.Route.getLoginPath()));
}

export function* onEnterPartnerUpgradeFlow() {
  let entitlementId = yield select(D.Utils.QueryParams.getEntitlementId);
  let tier;
  let partnerName;

  if (entitlementId) {
    const featureCode = yield select(D.Utils.QueryParams.getFeatureCode);
    const store = yield select(D.Utils.QueryParams.getStore);
    tier =
      PaymentsConstants.mapPartnerUpgradeFeatureCodeToTier[
        featureCode || PaymentsConstants.partnerUpgradeFeatureCodes.T3
      ];
    partnerName = store ? store.replace('<', '&lt;').replace('>', '&gt;') : 'AT&T';
  } else {
    entitlementId = yield select(PaymentsSelectors.getPartnerUpgradeEntitlementId);
    tier = yield select(PaymentsSelectors.getPartnerUpgradeTier);
    partnerName = yield select(PaymentsSelectors.getPartnerUpgradePartnerName);
  }

  yield put(
    PaymentsActions.initiatePartnerUpgrade({
      entitlementId,
      tier,
      partnerName,
    }),
  );
}

export function* onInitiatePartnerUpgrade() {
  const isAuthenticatedListener = yield select(D.Utils.Listener.isAuthenticatedListener);
  if (isAuthenticatedListener) {
    yield fork(Helpers.processAuthenticatedListenerForPartnerUpgrade);
  } else {
    yield call(Helpers.authenticateListenerForPartnerUpgrade);
  }
}

export function* onRequestPartnerUpgrade(action) {
  try {
    const vendorSku = PaymentsConstants.PARTNER_SKU;
    const getSpecialOfferType = PaymentsConstants.OfferTypes.PARTNER_SPECIAL_OFFER_TYPE;
    const specialOfferCode = yield select(PaymentsSelectors.getPartnerUpgradeEntitlementId);
    const tier = get(action, 'data.tier');
    const discountType = get(action, 'data.discountType');
    yield D.putAndWait(
      PaymentsActions.acquireOffer({
        vendorSku,
        getSpecialOfferType,
        specialOfferCode,
        tier,
        discountType,
      }),
    );
  } catch (error) {
    const errorType = error && error.errors && error.errors.length > 0 && error.errors[0].errorType;
    yield call(Helpers.handlePartnerUpgradeAcquireProductError, errorType);
  }
}

export function* determineDisplayUpgradePage() {
  const productTierIsPlus = yield select(PaymentsSelectors.productTierIsPlus);
  const productTierIsPremium = yield select(PaymentsSelectors.productTierIsPremium);
  const isOnUpgradePage = yield select(D.Utils.Route.isOnUpgradePage);

  let redirectPath;
  if (productTierIsPlus) {
    redirectPath = D.Utils.Route.getPremiumUpgradePath();
  } else if (productTierIsPremium) {
    redirectPath = D.Utils.Route.getFamilyPlanUpgradePath();
  } else {
    yield put(PaymentsActions.clearSelectedOffer());
    redirectPath = !isOnUpgradePage && D.Utils.Route.getUpgradePath();
  }

  if (redirectPath) {
    yield put(replace(redirectPath));
  } else {
    yield call(Helpers.logUpgradePage, {
      action: PaymentsConstants.PartnerUpgradeStatsActions.LANDING_VIEW,
      page_view: PaymentsConstants.Analytics.UPGRADE_LANDING_PAGE,
      view_mode: D.getInDomain('stats.constants.ViewModes.SETTINGS'),
    });
  }
}

export function* redirectToSubscription() {
  // Ensure that we have data required to render the payment/upgrade/subscription pages BEFORE we redirect users to those pages.
  // Note: these api calls are actually in flight already, causing duplicate api calls.
  const hasAvailableProducts = yield select(PaymentsSelectors.hasAvailableProducts);
  // if no products available, then pause. otherwise, continue
  if (!hasAvailableProducts) {
    // pause until these two calls succeed to provide the best user experience and the least amount of "flickering" due to state load, only if on first load
    yield all([
      D.tryCall(PaymentsServices.getAvailableProducts),
      D.tryCall(PaymentsServices.getCreditCardV2),
    ]);
  }

  const activeProductGroupTier = yield select(PaymentsSelectors.getActiveProductGroupTier);
  const activeProductGroupDiscountType = yield select(
    PaymentsSelectors.getActiveProductDiscountType,
  );

  const planName = PaymentsUtils.getPlanName(
    activeProductGroupTier,
    activeProductGroupDiscountType,
  );
  const msg = {
    ...PaymentsConstants.Messages.alreadySubscribedToSamePlan,
    values: { planName },
    messageName: PaymentsConstants.ErrorCodes.ALREADY_SUBSCRIBED,
  };

  const isResponsiveSubscriptionEnabled = yield select(
    D.Utils.TunerVars.getIsResponsiveSubscriptionEnabled,
  );
  if (!isResponsiveSubscriptionEnabled) {
    const isMobileDevice = yield select(D.Utils.Capabilities.isMobileDevice);

    if (isMobileDevice) {
      yield put(PaymentsActions.setMobileMessage({ message: msg }));
    }

    yield put(replace(D.Utils.Route.getSubscriptionPath()));

    if (!isMobileDevice) {
      yield put(PaymentsActions.clearSelectedOffer());
      yield put(D.Actions.UseCases.openErrorToast(msg));
    }
  } else {
    yield put(replace(D.Utils.Route.getSubscriptionPath()));
    yield put(PaymentsActions.clearSelectedOffer());
    yield put(D.Actions.UseCases.openErrorToast(msg));
  }
}

export function* completeUpgradeFlow(upgradeTier) {
  if (!upgradeTier) {
    yield fork(determineDisplayUpgradePage);
  }
}

export function* onProcessUpgradeAuthentication(action) {
  const { tierSlug, subTierSlug } = action.data;
  const upgradeTier = yield call(PaymentsUtils.getTierFromSlugs, tierSlug, subTierSlug);
  const upgradeDiscountType = yield call(PaymentsUtils.getDiscountTypeFromSubTierSlug, subTierSlug);

  const isInternallyTriggeredAnonUpgrade = yield select(PaymentsSelectors.getIsAnonymousUpgrade);
  yield put(PaymentsActions.setIsAnonymousUpgrade());
  yield put(
    PaymentsActions.selectOffer({
      tier: upgradeTier,
      discountType: upgradeDiscountType,
    }),
  );

  const hasSeenPlanDetails = yield select(PaymentsSelectors.getHasSeenPlanDetails);
  if (hasSeenPlanDetails) {
    const currentPath = yield select(D.Utils.Route.getCurrentPathnameFromState);
    yield put(D.Actions.Login.setAfterLoginRedirect(currentPath));

    const hasRegistrationQueryParam = yield select(D.Utils.QueryParams.getRegister);
    if (hasRegistrationQueryParam || isInternallyTriggeredAnonUpgrade) {
      yield put(replace(D.Utils.Route.getRegisterPath()));
    } else {
      yield put(replace(D.Utils.Route.getLoginPath()));
    }
  }
}

export function* onProcessUpgrade(action) {
  const { tierSlug, subTierSlug } = action.data;
  const upgradeTier = yield call(PaymentsUtils.getTierFromSlugs, tierSlug, subTierSlug);
  const upgradeDiscountType = yield call(PaymentsUtils.getDiscountTypeFromSubTierSlug, subTierSlug);
  const hasDeterminedPlan = !!upgradeTier;

  if (hasDeterminedPlan) {
    yield put(
      PaymentsActions.selectOffer({
        tier: upgradeTier,
        discountType: upgradeDiscountType,
      }),
    );
  }

  if (
    yield call(Helpers.shouldRedirectFromUpgradeToSubscription, upgradeTier, upgradeDiscountType)
  ) {
    yield call(redirectToSubscription);
    return;
  }
  if (!hasDeterminedPlan) {
    yield fork(determineDisplayUpgradePage);
    return;
  }

  let isVerified = true;
  const isApplyingDiscount = yield call(PaymentsUtils.isDiscount, upgradeDiscountType);
  if (isApplyingDiscount) {
    try {
      const eRequestId = yield select(D.Utils.QueryParams.getERequestId);
      if (eRequestId) {
        isVerified = yield call(Helpers.getIsERequestIdVerified, eRequestId);
      } else if (upgradeDiscountType) {
        const doesDiscountTypeRequireVerification = yield call(
          Helpers.doesDiscountTypeRequireVerification,
          upgradeDiscountType,
        );
        isVerified = !doesDiscountTypeRequireVerification;
      } else {
        yield call(Helpers.handleGenericSubscriptionFailureWithMessage, {
          errorString: 'Missing eRequestId and discountType from verification request',
          redirectPath: D.Utils.Route.getUpgradePath(),
        });
      }
    } catch (error) {
      const { message, errorString } = error;
      yield call(Helpers.handleGenericSubscriptionFailureWithMessage, {
        errorString: `Error calling verification service: ${errorString}, message: ${message}`,
        redirectPath: D.Utils.Route.getUpgradePath(),
      });
      return;
    }
  }

  if (!isVerified) {
    const hasSeenPlanDetails = yield select(PaymentsSelectors.getHasSeenPlanDetails);
    if (hasSeenPlanDetails) {
      const isStudentReverificationDenied4Year = yield select(
        PaymentsSelectors.isStudentReverificationDenied4Year,
      );
      if (!isStudentReverificationDenied4Year) {
        yield put(
          PaymentsActions.redirectToDiscountVerification({
            tierSlug,
            verificationType:
              PaymentsUtils.getVerificationTypeFromDiscountType(upgradeDiscountType),
          }),
        );
      }
    } else {
      yield call(completeUpgradeFlow, upgradeTier);
      const currentPath = yield select(D.Utils.Route.getCurrentPathnameFromState);
      const redirectPath = D.Utils.Route.getUpgradePath(tierSlug, subTierSlug);
      // Without this guard, the route onChange will be triggered infinitely.
      if (redirectPath !== currentPath) {
        yield put(replace(redirectPath));
      }
    }
    return;
  }

  yield call(completeUpgradeFlow, upgradeTier);

  const isMobileDevice = yield select(D.Utils.Capabilities.isMobileDevice);
  const redirectPath = D.Utils.Route.getPaymentPath(tierSlug, subTierSlug);

  yield fork(PaymentsUtils.initClientProfiler);

  if (redirectPath && !isMobileDevice) {
    yield put(replace(redirectPath));
  }
}

export function* onEnterSubscriptionManagement() {
  if (yield select(PaymentsSelectors.isActiveProductDiscountTypeStudent)) {
    yield call(
      D.putAndWait,
      PaymentsActions.getDiscountVerificationInfo({
        discountType: PaymentsConstants.discountTypes.STUDENT,
      }),
    );
    if (yield select(PaymentsSelectors.isInStudentReverificationPeriod)) {
      yield call(Helpers.logSubscriptionWebViewEvent, PaymentsConstants.Analytics.STUDENT_REVERIFY);
    } else {
      yield call(Helpers.logSubscriptionWebViewEvent, PaymentsConstants.Analytics.STUDENT_ACTIVE);
    }
  }
  const productTierIsAdSupported = yield select(PaymentsSelectors.productTierIsAdSupported);
  const previousTier = yield select(PaymentsSelectors.getPreviousTier);
  const didJustUpgradeFromAdSupported = previousTier === PaymentsConstants.tiers.AD_SUPPORTED;
  if (productTierIsAdSupported && !didJustUpgradeFromAdSupported) {
    yield put(push(D.Utils.Route.getUpgradePath()));
  } else {
    yield fork(PaymentsUtils.initClientProfiler);
  }
}

export function* onOpenSwitchPlanModal({ data }) {
  // @todo WEBCLIENT-8592 clearSuccessMessage() is needed to clear the `userAccount.updateSuccess` flag.
  // So long as `userAccount.updateSuccess` is set, modals will unexpectedly close.
  yield put(D.Actions.UserAccount.clearSuccessMessage());

  const tierSelected = data.selectedTier;
  const discountTypeSelected = data.selectedDiscountType;
  const durationTypeSelected = data.selectedDurationType;
  const isAgeIneligible = yield select(PaymentsSelectors.isAgeIneligible);

  if (PaymentsUtils.isPremiumFamilyTier(tierSelected) && isAgeIneligible) {
    yield put(push(D.Utils.Route.getUpgradePath()));
    return;
  }

  const modalProps = {
    modalType: 'ModalCustom',
    body: (
      <SwitchPlanConfirmationModal
        tierSelected={tierSelected}
        discountTypeSelected={discountTypeSelected}
        durationTypeSelected={durationTypeSelected}
      />
    ),
  };

  // @todo WEBCLIENT-8592 clearSuccessMessage() is needed to clear the `userAccount.updateSuccess` flag.
  // So long as `userAccount.updateSuccess` is set, modals will unexpectedly close.
  yield put(D.Actions.UserAccount.clearSuccessMessage());
  yield put(D.Actions.Modal.openModal(modalProps));
}

export function* onOpenCancelSubscriptionModal() {
  // @todo WEBCLIENT-8592 clearSuccessMessage() is needed to clear the `userAccount.updateSuccess` flag.
  // So long as `userAccount.updateSuccess` is set, modals will unexpectedly close.
  yield put(D.Actions.UserAccount.clearSuccessMessage());

  const modalProps = {
    modalType: 'ModalCustom',
    body: SubscriptionModal,
    isCanceling: true,
    subscriptionInfoV2: yield select(PaymentsSelectors.getSubscriptionInfoV2),
  };

  const paymentProvider = yield select(PaymentsSelectors.getPaymentProvider);
  if (paymentProvider === PaymentsConstants.PaymentProviders.PAYPAL) {
    modalProps.isUpdatingPaypal = true;
  }

  yield put(D.Actions.Modal.openModal(modalProps));
  yield call(Helpers.logSubscriptionWebViewEvent, PaymentsConstants.Analytics.CANCEL_SUBSCRIPTION);
}

export function* onSwitchPlan({ data }) {
  const { tier, discountType, durationType, paymentProvider } = data;

  yield put(
    D.Actions.UseCases.webViewModeInteraction({
      page_view: PaymentsConstants.Analytics.SUBSCRIPTION_SWITCH_PLAN,
      view_mode: PaymentsUtils.getViewModePlanName(tier, discountType),
    }),
  );

  let discountTypeRequiresVerification;

  try {
    discountTypeRequiresVerification = yield call(
      Helpers.doesDiscountTypeRequireVerification,
      discountType,
    );
  } catch (error) {
    yield call(Helpers.handleGenericSubscriptionFailureWithMessage, {
      errorString: 'Failed getting discount verification info when switching plans',
    });
    return;
  }

  if (discountTypeRequiresVerification) {
    const verificationType = yield call(
      PaymentsUtils.getVerificationTypeFromDiscountType,
      discountType,
    );
    const tierSlug = yield call(PaymentsUtils.getTierSlugFromTier, tier);
    yield put(push(D.Utils.Route.getDiscountVerificationPath(tierSlug, verificationType)));
    return;
  }

  const isPaypal = yield call(PaymentsUtils.isPaypal, paymentProvider);
  if (isPaypal) {
    yield put(
      PaymentsActions.prepareToAcquireOffer({
        tier,
        discountType,
        paymentProvider,
        durationType,
      }),
    );
  } else {
    yield put(
      PaymentsActions.acquireOffer({
        tier,
        discountType,
        paymentProvider,
        durationType,
      }),
    );
  }
}

export function* onAcquireOffer({ data }) {
  yield put(
    PaymentsActions.logUpgradePageEvent({
      action: `${PaymentsConstants.PaymentsStatsActions.CREDIT_CARD_CLICK}${PaymentsConstants.PaymentsStatsSuffixes.PAYMENT_ATTEMPT}`,
    }),
  );

  const props = yield call(Helpers.getAcquireOfferProps, data);
  const tier = data.tier.toLowerCase();
  try {
    yield call(PaymentsServices.acquireProduct, props);
    yield put(
      PaymentsActions.logUpgradePageEvent({
        action: `${tier}${PaymentsConstants.PaymentsStatsSuffixes.PAYMENT_SUCCESS}`,
      }),
    );
    yield call(Helpers.logSubscriptionResults, {
      suffix: PaymentsConstants.PageViewSuffixes.SUCCESS,
      specialOfferCode: props.specialOfferToken,
    });

    const isGFUpgrade = yield select(D.Utils.QueryParams.getGreenfieldUpgradeFlag);
    if (isGFUpgrade) {
      // @todo GRNWEB-4689 - This is hacky but it will be removed shortly anyway.
      // Note, local dev environments will redirect to m-t
      const gfUrl = D.Utils.Url.isProductionUrl()
        ? 'https://player.pandora.com'
        : 'https://mt-player.savagebeast.com';
      D.Utils.Route.navigate(gfUrl);
    }
  } catch (error) {
    const errorType = get(error, 'errorType');
    const isRecognizedError = [PaymentsConstants.ErrorCodes.CREDIT_CARD_DECLINED_PAYMENT].includes(
      errorType,
    );
    if (!isRecognizedError) {
      yield put(
        D.Actions.UseCases.openErrorToast({
          ...PaymentsConstants.Messages.somethingHasGoneWrong,
          messageName: D.Constants.Analytics.UserMessages.GENERIC_SUBSCRIPTION_FAILURE,
        }),
      );
    }

    yield put(
      PaymentsActions.logUpgradePageEvent({
        action: `${tier}${PaymentsConstants.PaymentsStatsSuffixes.PAYMENT_FAIL}`,
      }),
    );
    yield call(Helpers.logSubscriptionResults, {
      suffix: PaymentsConstants.PageViewSuffixes.FAIL,
      specialOfferCode: props.specialOfferToken,
    });

    throw error;
  } finally {
    const isCreditCardPurchase = !!get(props, 'creditCardInfo.cardNumber');
    if (isCreditCardPurchase) {
      yield call(PaymentsUtils.renewClientProfile);
    }
  }
}

export function* onPrepareToAcquireOffer({ data }) {
  yield put(
    PaymentsActions.logUpgradePageEvent({
      action: `${PaymentsConstants.PaymentsStatsActions.PAYPAL_CLICK}${PaymentsConstants.PaymentsStatsSuffixes.PAYMENT_ATTEMPT}`,
    }),
  );

  const props = yield call(Helpers.getAcquireOfferProps, data);
  const oldPaypalUrl = yield select(PaymentsSelectors.getPaypalRedirectUrl);

  try {
    yield call(PaymentsServices.prepareToAcquirePaypalProduct, props);
  } catch (error) {
    log.error(
      { apiError: error },
      `Failed request to ${get(error, 'action.endpoint')}: ${error.status} - ${error.errorMessage}`,
    );
  }

  const updatedPaypalUrl = yield select(PaymentsSelectors.getPaypalRedirectUrl);
  if (!updatedPaypalUrl) {
    yield put(
      D.Actions.UseCases.openErrorToast(
        PaymentsConstants.Messages.somethingHasGoneWrongRefreshRetry,
      ),
    );
  } else if (oldPaypalUrl !== updatedPaypalUrl) {
    yield call(window.open, updatedPaypalUrl, '_self');
  }
}

export function* onSelectPaymentProvider(action) {
  const paymentProvider = get(action, 'data.paymentProvider');
  const actionName = PaymentsConstants.mapPaymentProviderToStatsAction[paymentProvider];

  if (!actionName) {
    return;
  }

  yield put(
    PaymentsActions.logUpgradePageEvent({
      action: actionName,
    }),
  );
}

export function* onWaitForSubscriptionInfo(action) {
  const isLoaded = yield select(PaymentsSelectors.isSubscriptionInfoLoaded);
  if (!isLoaded) {
    yield race({
      success: take(D.Constants.Api.ApiActions.GET_SUBSCRIPTION_INFO_V2_SUCCESS),
      fail: take(D.Constants.Api.ApiActions.GET_SUBSCRIPTION_INFO_V2_FAIL),
      timeout: delay(10000),
    });
  }
}

export function* OnReverifyLinkClicked() {
  yield put(
    D.Actions.UseCases.webViewModeInteraction({
      event_name: D.getInDomain('stats.constants.Events.UPGRADE_PAGE'),
      page_view: D.getInDomain('stats.constants.ViewModes.SUBSCRIPTION'),
      view_mode: PaymentsConstants.Analytics.STUDENT_REVERIFY,
      action: PaymentsConstants.Analytics.REVERIFY_NOW,
    }),
  );
}
